import { useMemo, type PropsWithChildren } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";

import {
  type UniversalBenchmarksStage,
  type BenchmarkIndicatorRow,
} from "./types";
import { UniversalBenchmarksTable } from "./universal-benchmarks-table";
import { filterIndicatorRowsByStage, isPositiveChange } from "./utils";

interface Props {
  iconsSize?: "sm" | "md";
  variant?: "dial" | "badge";
  className?: string;
  preferredStage?: UniversalBenchmarksStage;
  benchmarkItemName?: string;
  benchmarkIndicatorRows: BenchmarkIndicatorRow[];
}

export const UniversalBenchmarksIndicator = ({
  iconsSize = "md",
  variant = "dial",
  preferredStage,
  className,
  benchmarkItemName,
  benchmarkIndicatorRows,
  children,
}: PropsWithChildren<Props>) => {
  const { isAllPositiveChange, isAllNegativeChange, isMixedChange } =
    useMemo(() => {
      const stageRows = filterIndicatorRowsByStage(
        benchmarkIndicatorRows,
        preferredStage,
      );

      const isAllPositiveChange = stageRows.every((row) =>
        isPositiveChange(row),
      );

      const isAllNegativeChange = stageRows.every(
        (row) => !isPositiveChange(row),
      );

      return {
        isAllPositiveChange,
        isAllNegativeChange,
        isMixedChange: !isAllPositiveChange && !isAllNegativeChange,
      };
    }, [benchmarkIndicatorRows, preferredStage]);

  return (
    <GenericTooltip
      className={className}
      noInfoCircle
      overlayClassName={clsx(
        "pointer-events-none p-0 rounded-lg border-none shadow-[0_4px_8px] shadow-v2-black/15%",
      )}
      arrowClassName="bg-v2-secondary border-none"
      placement="top"
      content={
        <div className="p-4 bg-v2-secondary rounded-lg text-v2-primary">
          <div className="mb-4">
            <div className="text-sm font-semibold mb-1.5">Benchmark</div>
            {benchmarkItemName && (
              <div className="text-2xs flex items-center gap-1">
                <span className="inline-block font-semibold max-w-[240px] truncate">
                  {benchmarkItemName}
                </span>
                <span>performance relative to benchmark.</span>
              </div>
            )}
          </div>
          <UniversalBenchmarksTable
            rows={benchmarkIndicatorRows}
            preferredStage={preferredStage}
          />
        </div>
      }
    >
      {variant === "badge" ? (
        <div
          className={clsx(
            "font-mono text-xs rounded h-5 flex items-center px-1.5 text-nowrap",
            {
              "text-v2-action-positive-hover bg-v2-alien/400/10%":
                isAllPositiveChange,
              "text-v2-action-negative-hover bg-v2-mars/400/10%":
                isAllNegativeChange,
              "text-v2-sun/600 bg-v2-sun/200": isMixedChange,
            },
          )}
        >
          {children}
        </div>
      ) : (
        <>
          {children}
          <div
            className={clsx(
              "shrink-0 rounded-full flex items-center justify-center",
              {
                "w-5 h-5": iconsSize === "sm",
                "w-6 h-6": iconsSize === "md",
              },
            )}
          >
            <i
              className={clsx("fa-light", {
                "text-[13px]": iconsSize === "sm",
                "text-base": iconsSize === "md",
                "fa-gauge-high text-v2-alien/500": isAllPositiveChange,
                "fa-gauge-low text-v2-mars/500": isAllNegativeChange,
                "fa-gauge text-v2-sun/600": isMixedChange,
              })}
            />
          </div>
        </>
      )}
    </GenericTooltip>
  );
};
